/**
 * A person's own arrangement of the sidebar's "By product" section: order,
 * hidden products and an icon per product. Kept per user in localStorage; the
 * catalog stays the source of which products exist.
 */
export interface ProductNavPrefs {
  /** Product keys in the person's order; keys no longer in the catalog are ignored. */
  order: string[]
  hidden: string[]
  /** Product key → icon name from PRODUCT_ICON_NAMES. */
  icons: Record<string, string>
}

export const EMPTY_PRODUCT_NAV: ProductNavPrefs = { order: [], hidden: [], icons: {} }

/** The icons a person can pick; the component layer maps each name to a lucide icon. */
export const PRODUCT_ICON_NAMES = [
  'dna',
  'flask',
  'microscope',
  'test-tube',
  'syringe',
  'heart-pulse',
  'shield-check',
  'clipboard-check',
  'file-text',
  'graduation-cap',
  'plug',
  'server',
  'key',
  'boxes',
  'factory',
  'truck',
  'star',
  'folder',
] as const

export type ProductIconName = (typeof PRODUCT_ICON_NAMES)[number]

export const isProductIcon = (name: string | undefined): name is ProductIconName => !!name && (PRODUCT_ICON_NAMES as readonly string[]).includes(name)

/**
 * The catalog's products in the person's order: ordered keys first, then any
 * product they haven't placed yet (e.g. one added in admin) in catalog order.
 */
export function arrangeProducts<P extends { key: string }>(products: readonly P[], prefs: ProductNavPrefs): { product: P; icon: ProductIconName | null; hidden: boolean }[] {
  const byKey = new Map(products.map((p) => [p.key, p]))
  const placed = prefs.order.filter((k, i) => byKey.has(k) && prefs.order.indexOf(k) === i)
  const rest = products.map((p) => p.key).filter((k) => !placed.includes(k))
  return [...placed, ...rest].map((k) => {
    const icon = prefs.icons[k]
    return { product: byKey.get(k)!, icon: isProductIcon(icon) ? icon : null, hidden: prefs.hidden.includes(k) }
  })
}

/** `keys` with the item at `from` moved to `to` (both clamped). */
export function moveKey(keys: readonly string[], from: number, to: number): string[] {
  const next = [...keys]
  if (from < 0 || from >= next.length) return next
  const [item] = next.splice(from, 1)
  next.splice(Math.max(0, Math.min(to, next.length)), 0, item)
  return next
}

/** Reads stored prefs, dropping anything malformed rather than failing the sidebar. */
export function parseProductNav(raw: string | null): ProductNavPrefs {
  if (!raw) return EMPTY_PRODUCT_NAV
  try {
    const v = JSON.parse(raw) as Partial<ProductNavPrefs>
    const strings = (a: unknown) => (Array.isArray(a) ? a.filter((x): x is string => typeof x === 'string') : [])
    const icons: Record<string, string> = {}
    if (v.icons && typeof v.icons === 'object') for (const [k, i] of Object.entries(v.icons)) if (isProductIcon(i)) icons[k] = i
    return { order: strings(v.order), hidden: strings(v.hidden), icons }
  } catch {
    return EMPTY_PRODUCT_NAV
  }
}
